/* Voices (CONTEXT.md "Voices"; ADR 0028) — the agency-level page beside Bulk Media.

   One section lists every voice you can wire (stock voices, and the custom voices you
   cloned) above every Account whose voice you may set: pick a voice per row, or select
   Accounts and set one voice on all of them. An Account has one voice, the same one the AI
   speaks its PPV voice notes with; setting it never turns the AI's voice notes on. The Live
   test speaks a line for one Account in any of your voices and uses one voice note from
   that Account's quota. Voices are labels only: the provider never shows (white-label). */

import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'
import { Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PageHeading } from '../components/PageHeader'
import { Badge, Btn, Card, ErrorLine, Icon, InfoLine, Loading, Seg, Spin, Stack, TextArea, Warn } from '../components/ChatAssistant/compact/kit'
import { C } from '../components/ChatAssistant/compact/tokens'
import { MAX_TEST_TEXT } from '../components/ChatAssistant/compact/audio/model'
import { CloneCard, PreviewButton, QuotaBar, RunCard, type Run } from '../components/ChatAssistant/compact/audio/shared'
import { capStyle, iconBtn } from '../components/ChatAssistant/compact/audio/styles'
import { usePreview } from '../components/ChatAssistant/compact/audio/usePreview'
import type { LibraryVoice, Voice, VoiceAccount, VoiceKind, VoiceLibrary, VoiceRef, VoiceRegister } from '../api/client'
import { useVoicesPageAccess } from './access'
import { voicesApi, type VoicesApi } from './api'
import { Picker } from './Picker'
import './voices.css'

const POLL_MS = 5000
/** Rows a list shows before Show all. */
const FIRST = 20
const NONE = 'none'

const keyOf = (v: { kind: VoiceKind; id: string }) => `${v.kind}:${v.id}`
const refOf = (key: string): { kind: VoiceKind; id: string } | null => {
  if (key === NONE) return null
  const [kind, id] = key.split(':') as [VoiceKind, string]
  return { kind, id }
}
const fold = (s: string) => s.toLocaleLowerCase()

/** The first FIRST rows of a list, and the "Show all N" / "Show first N" toggle under it. */
function useFirst<T>(rows: T[]) {
  const [all, setAll] = useState(false)
  return { rows: all ? rows : rows.slice(0, FIRST), more: rows.length > FIRST ? { all, count: rows.length, toggle: () => setAll((v) => !v) } : null }
}

function ShowAll({ more }: { more: ReturnType<typeof useFirst>['more'] }) {
  const { t } = useTranslation('voices')
  if (!more) return null
  return (
    <button type="button" className="vo-showall" aria-expanded={more.all} onClick={more.toggle}>
      {more.all ? t('showLess', { n: FIRST }) : t('showAll', { count: more.count })}
      <Icon name={more.all ? 'chevron-up' : 'chevron-down'} size={13} />
    </button>
  )
}

export function VoicesPage() {
  const access = useVoicesPageAccess()
  if (access === null) return null
  if (!access) return <Navigate to="/" replace />
  return <VoicesView api={voicesApi} />
}

export function VoicesView({ api }: { api: VoicesApi }) {
  const { t } = useTranslation('voices')
  const [library, setLibrary] = useState<VoiceLibrary | null>(null)
  const [accounts, setAccounts] = useState<VoiceAccount[] | null>(null)
  const [loadError, setLoadError] = useState('')

  const loadLibrary = useCallback(() => api.library().then(setLibrary, (e: unknown) => setLoadError(String((e as Error)?.message ?? e))), [api])
  const loadAccounts = useCallback(() => api.accounts().then(setAccounts, (e: unknown) => setLoadError(String((e as Error)?.message ?? e))), [api])
  useEffect(() => {
    void loadLibrary()
    void loadAccounts()
  }, [loadLibrary, loadAccounts])

  // A clone is processing: poll until it is ready (or failed).
  const processing = !!library?.voices.some((v) => v.status === 'processing')
  useEffect(() => {
    if (!processing) return
    const h = window.setInterval(() => void loadLibrary(), POLL_MS)
    return () => window.clearInterval(h)
  }, [processing, loadLibrary])

  /** Rows the server answered after a change replace the old ones; counts follow. */
  const applyRows = (rows: VoiceAccount[]) => {
    const fresh = new Map(rows.map((r) => [r.account_id, r]))
    setAccounts((cur) => cur && cur.map((a) => fresh.get(a.account_id) ?? a))
    void loadLibrary()
  }

  return (
    <div className="mobile-page-shell cpt-root vo-page w-full px-6 pt-5 pb-6 min-w-0">
      <PageHeading
        title={
          <span className="vo-head">
            {t('title')}
            <Badge>{t('beta')}</Badge>
          </span>
        }
        subtitle={t('subtitle')}
      />
      {loadError && !library && !accounts && <ErrorLine>{loadError}</ErrorLine>}
      <div className="vo-layout">
        <VoicesSection api={api} library={library} accounts={accounts} onLibrary={setLibrary} reloadLibrary={loadLibrary} reloadAccounts={loadAccounts} onRows={applyRows} />
        <LiveTest api={api} library={library} accounts={accounts} onQuota={(id, quota) => setAccounts((cur) => cur && cur.map((a) => (a.account_id === id ? { ...a, quota } : a)))} />
      </div>
    </div>
  )
}

/* ── Voices + Accounts (one section) ─────────────────────────────── */
function VoicesSection({ api, library, accounts, onLibrary, reloadLibrary, reloadAccounts, onRows }: {
  api: VoicesApi
  library: VoiceLibrary | null
  accounts: VoiceAccount[] | null
  onLibrary: (fn: (l: VoiceLibrary | null) => VoiceLibrary | null) => void
  reloadLibrary: () => Promise<void>
  reloadAccounts: () => Promise<void>
  onRows: (rows: VoiceAccount[]) => void
}) {
  const { t } = useTranslation('voices')
  const preview = usePreview((v) => api.previewUrl(v.kind, v.id))
  const [cloning, setCloning] = useState(false)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState<string | null>(null)

  const remove = async (v: LibraryVoice) => {
    const msg = v.account_count ? t('voices.deleteUsed', { name: v.label, count: v.account_count }) : t('voices.deleteConfirm', { name: v.label })
    if (!window.confirm(msg)) return
    setDeleting(v.id)
    setError('')
    try {
      await api.remove(v.id)
      if (filter === keyOf(v)) setFilter(null)
      await Promise.all([reloadLibrary(), reloadAccounts()])
    } catch (e) {
      setError(e instanceof Error ? e.message : t('voices.deleteFailed'))
    } finally {
      setDeleting(null)
    }
  }

  const voices = useFirst(library?.voices ?? [])

  return (
    <section className="vo-card" aria-label={t('voices.title')}>
      <header className="vo-card-head">
        <h2 className="vo-h2">{t('voices.title')}</h2>
        {library && <span className="vo-muted">{t('voices.yours', { n: library.custom_count, max: library.custom_limit })}</span>}
        <span style={{ flex: 1 }} />
        {library && !cloning && (
          <Btn
            kind="tint"
            icon="mic"
            disabled={!library.can_clone}
            title={library.can_clone ? undefined : library.voice_service_ready ? t('voices.limit', { max: library.custom_limit }) : t('voices.unavailable')}
            onClick={() => setCloning(true)}
          >
            {t('voices.clone')}
          </Btn>
        )}
      </header>
      {cloning && (
        <CloneCard
          clone={api.clone}
          onCancel={() => setCloning(false)}
          onCreated={(v: Voice) => {
            setCloning(false)
            onLibrary((cur) => cur && { ...cur, voices: [...cur.voices, { ...v, account_count: 0 }], custom_count: cur.custom_count + 1, can_clone: cur.custom_count + 1 < cur.custom_limit })
          }}
        />
      )}
      {preview.error && <Warn>{preview.error}</Warn>}
      {error && <Warn>{error}</Warn>}
      {!library ? (
        <Loading />
      ) : (
        <Card>
          {voices.rows.map((v) => (
            <div key={keyOf(v)} className="vo-voice">
              <span className="vo-voice-name">
                <span className="vo-strong">{v.label}</span>
                <span className="vo-muted">
                  {v.status === 'processing' ? (
                    <>
                      <Spin size={10} /> {t('voices.processing')}
                    </>
                  ) : v.status === 'failed' ? (
                    t('voices.failed')
                  ) : v.kind === 'stock' ? (
                    t('voices.stock')
                  ) : (
                    t('voices.mine')
                  )}
                </span>
              </span>
              <button
                type="button"
                className="vo-count"
                disabled={!v.account_count}
                aria-pressed={filter === keyOf(v)}
                title={v.account_count ? t('voices.showAccounts') : undefined}
                onClick={() => setFilter((cur) => (cur === keyOf(v) ? null : keyOf(v)))}
              >
                {t('voices.accounts', { count: v.account_count })}
              </button>
              {v.status === 'ready' && library.voice_service_ready && (
                <PreviewButton state={preview.stateOf(v)} onClick={() => void preview.toggle(v)} />
              )}
              {v.owned && (
                <button type="button" title={t('voices.delete')} disabled={deleting === v.id} onClick={() => void remove(v)} className="cpt-hover-text" style={iconBtn}>
                  {deleting === v.id ? <Spin size={12} /> : <Icon name="trash-2" size={13} />}
                </button>
              )}
            </div>
          ))}
          <ShowAll more={voices.more} />
        </Card>
      )}
      {library && !library.voice_service_ready && <InfoLine>{t('voices.serviceOff')}</InfoLine>}
      <AccountList api={api} library={library} accounts={accounts} filter={filter} onClearFilter={() => setFilter(null)} onRows={onRows} />
    </section>
  )
}

function AccountList({ api, library, accounts, filter, onClearFilter, onRows }: {
  api: VoicesApi
  library: VoiceLibrary | null
  accounts: VoiceAccount[] | null
  filter: string | null
  onClearFilter: () => void
  onRows: (rows: VoiceAccount[]) => void
}) {
  const { t } = useTranslation('voices')
  const [q, setQ] = useState('')
  const [selected, setSelected] = useState<string[]>([])
  const [bulkVoice, setBulkVoice] = useState('')
  const [saving, setSaving] = useState<string | null>(null)
  const [error, setError] = useState('')
  const picked = useMemo(() => new Set(selected), [selected])

  const ready = (library?.voices ?? []).filter((v) => v.status === 'ready')
  const shown = useMemo(() => {
    const query = fold(q.trim())
    return (accounts ?? []).filter(
      (a) => (!query || [a.name, a.username].some((s) => fold(s).includes(query))) && (!filter || (a.voice && keyOf(a.voice) === filter)),
    )
  }, [accounts, q, filter])
  const first = useFirst(shown)
  const filterLabel = filter ? library?.voices.find((v) => keyOf(v) === filter)?.label : null

  const save = async (ids: string[], key: string, busy: string) => {
    setSaving(busy)
    setError('')
    try {
      onRows(await api.setVoice(ids, refOf(key)))
      return true
    } catch (e) {
      setError(e instanceof Error ? e.message : t('accounts.saveFailed'))
      return false
    } finally {
      setSaving(null)
    }
  }

  const on = shown.filter((a) => picked.has(a.account_id)).length
  const toggleShown = () => {
    const ids = shown.map((a) => a.account_id)
    if (on === shown.length) setSelected((cur) => cur.filter((id) => !ids.includes(id)))
    else setSelected((cur) => [...cur, ...ids.filter((id) => !picked.has(id))])
  }
  const toggle = (id: string) => setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]))

  /** A row's choices: none, your ready voices, and its current voice when it isn't yours. */
  const optionsFor = (current: VoiceRef | null) => {
    const opts = [{ id: NONE, label: t('accounts.none') }, ...ready.map((v) => ({ id: keyOf(v), label: v.label }))]
    if (current && !opts.some((o) => o.id === keyOf(current))) opts.push({ id: keyOf(current), label: t('accounts.someoneElses', { name: current.label }) })
    return opts
  }

  if (!accounts) return <Loading />
  return (
    <div className="vo-accounts">
      <div className="vo-toolbar">
        <h3 className="vo-h3">{t('accounts.title')}</h3>
        <label className="vo-search">
          <Icon name="search" size={13} color={C.muted} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('accounts.search')} aria-label={t('accounts.search')} />
        </label>
        <button type="button" className="vo-link" onClick={toggleShown} disabled={!shown.length}>
          {on === shown.length && shown.length ? t('accounts.unselectAll') : t('accounts.selectAll')}
        </button>
        {filterLabel && (
          <button type="button" className="vo-chip" onClick={onClearFilter} title={t('accounts.clearFilter')}>
            {t('accounts.using', { name: filterLabel })}
            <Icon name="x" size={11} />
          </button>
        )}
      </div>
      {selected.length > 0 && (
        <div className="vo-bulk" role="group" aria-label={t('accounts.setVoice')}>
          <span className="vo-strong">{t('accounts.selected', { count: selected.length })}</span>
          <span className="vo-muted">{t('accounts.setVoice')}</span>
          <Picker value={bulkVoice} options={optionsFor(null)} onChange={setBulkVoice} placeholder={t('accounts.choose')} label={t('accounts.setVoice')} />
          <Btn
            kind="primary"
            disabled={!bulkVoice || saving === 'bulk'}
            onClick={async () => {
              if (await save(selected, bulkVoice, 'bulk')) {
                setSelected([])
                setBulkVoice('')
              }
            }}
          >
            {saving === 'bulk' ? <Spin size={11} /> : t('accounts.apply')}
          </Btn>
          <button type="button" className="vo-link" onClick={() => setSelected([])}>
            {t('accounts.clear')}
          </button>
        </div>
      )}
      <p className="vo-muted vo-note">{t('accounts.note')}</p>
      {error && <Warn>{error}</Warn>}
      <Card>
        {!shown.length && <div className="vo-empty">{accounts.length ? t('accounts.noMatch') : t('accounts.empty')}</div>}
        {first.rows.map((a) => (
          <div key={a.account_id} className={`vo-account ${picked.has(a.account_id) ? 'is-picked' : ''}`}>
            <input type="checkbox" checked={picked.has(a.account_id)} onChange={() => toggle(a.account_id)} aria-label={a.name} style={{ accentColor: C.purple }} />
            <span className="vo-account-name">
              <span className="vo-strong">{a.name}</span>
              <span className="vo-muted">
                {a.username ? `@${a.username}` : a.account_id}
                {a.platform === 'fansly' && <> · {t('accounts.fansly')}</>}
                {a.voice_notes_enabled && <> · {t('accounts.aiOn')}</>}
              </span>
            </span>
            <span className="vo-muted vo-quota" title={t('accounts.quotaTip', { ...a.quota })}>
              {t('accounts.left', { n: a.quota.remaining })}
            </span>
            <Picker
              value={a.voice ? keyOf(a.voice) : NONE}
              options={optionsFor(a.voice)}
              disabled={saving === a.account_id}
              onChange={(id) => void save([a.account_id], id, a.account_id)}
              label={t('accounts.voiceOf', { name: a.name })}
            />
            {saving === a.account_id && <Spin size={11} />}
          </div>
        ))}
        <ShowAll more={first.more} />
      </Card>
    </div>
  )
}

/* ── Live test ───────────────────────────────────────────────────── */
let runSeq = 0

function LiveTest({ api, library, accounts, onQuota }: {
  api: VoicesApi
  library: VoiceLibrary | null
  accounts: VoiceAccount[] | null
  onQuota: (accountId: string, quota: VoiceAccount['quota']) => void
}) {
  const { t } = useTranslation('voices')
  const [accountId, setAccountId] = useState('')
  const [voice, setVoice] = useState('')
  const [register, setRegister] = useState<VoiceRegister>('hot')
  const [text, setText] = useState('')
  const [running, setRunning] = useState(false)
  const [error, setError] = useState('')
  const [runs, setRuns] = useState<Run[]>([])

  const account = accounts?.find((a) => a.account_id === accountId) ?? null
  const ready = (library?.voices ?? []).filter((v) => v.status === 'ready')
  const options = [...ready.map((v) => ({ id: keyOf(v), label: v.label }))]
  if (account?.voice && !options.some((o) => o.id === keyOf(account.voice!))) options.unshift({ id: keyOf(account.voice), label: account.voice.label })
  const chosen = options.find((o) => o.id === voice) ?? null

  const pickAccount = (id: string) => {
    setAccountId(id)
    const a = accounts?.find((x) => x.account_id === id)
    // Its own voice first; any of yours can be tried instead.
    setVoice(a?.voice ? keyOf(a.voice) : ready[0] ? keyOf(ready[0]) : '')
  }

  const blocker = !account ? 'account' : !chosen ? 'voice' : account.quota.remaining <= 0 ? 'quota' : library && !library.voice_service_ready ? 'service' : null

  const run = async () => {
    if (!account || !chosen) return
    const ref = refOf(chosen.id)!
    setRunning(true)
    setError('')
    try {
      const result = await api.test(account.account_id, {
        text,
        voice_register: register,
        voice_id: ref.kind === 'stock' ? ref.id : null,
        custom_voice_id: ref.kind === 'custom' ? ref.id : null,
        normal_tone_notes: '',
        hot_tone_notes: '',
        allowed_languages: [],
      })
      onQuota(account.account_id, result.quota)
      setRuns((r) => [{ id: ++runSeq, at: new Date(), register, voice: `${chosen.label} · ${account.name}`, result }, ...r].slice(0, 10))
    } catch (e) {
      const msg = e instanceof Error ? e.message : ''
      setError(msg === 'REQUEST_TIMEOUT' ? t('test.timeout') : msg || t('test.failed'))
    } finally {
      setRunning(false)
    }
  }

  return (
    <section className="vo-card vo-test" aria-label={t('test.title')}>
      <header className="vo-card-head">
        <h2 className="vo-h2">{t('test.title')}</h2>
      </header>
      <p className="vo-muted vo-note">{t('test.hint')}</p>
      <Stack gap={12}>
        <div className="vo-test-pickers">
          <div style={fieldCol}>
            <span style={capStyle}>{t('test.account')}</span>
            <Picker
              wide
              value={accountId}
              options={(accounts ?? []).map((a) => ({ id: a.account_id, label: a.name, sub: a.username ? `@${a.username}` : undefined }))}
              onChange={pickAccount}
              placeholder={t('test.chooseAccount')}
              label={t('test.account')}
              filterPlaceholder={t('accounts.search')}
            />
          </div>
          <div style={fieldCol}>
            <span style={capStyle}>{t('test.voice')}</span>
            <Picker
              wide
              value={voice}
              options={options.map((o) => (account?.voice && o.id === keyOf(account.voice) ? { ...o, label: t('test.accountVoice', { name: o.label }) } : o))}
              disabled={!account}
              onChange={setVoice}
              placeholder={t('test.chooseVoice')}
              label={t('test.voice')}
            />
          </div>
        </div>
        {account && <QuotaBar quota={account.quota} />}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={capStyle}>{t('test.register')}</span>
          <Seg options={[{ id: 'normal', label: t('test.normal') }, { id: 'hot', label: t('test.hot') }]} value={register} onChange={setRegister} />
        </div>
        <label style={fieldCol}>
          <span style={capStyle}>{t('test.text')}</span>
          <TextArea value={text} onChange={setText} placeholder={t('test.textPh')} height={80} />
          <span className="vo-muted">{t('test.textHelp')}</span>
          {text.length > MAX_TEST_TEXT && <Warn>{t('test.textTooLong', { max: MAX_TEST_TEXT })}</Warn>}
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Btn kind="primary" icon={running ? undefined : 'play-circle'} height={30} disabled={running || !!blocker || !text.trim() || text.length > MAX_TEST_TEXT} onClick={run}>
            {running ? (
              <>
                <Spin size={11} />
                {t('test.running')}
              </>
            ) : (
              t('test.run')
            )}
          </Btn>
          {blocker ? <span className="vo-muted">{t(`test.block.${blocker}`)}</span> : <span className="vo-muted">{t('test.cost', { account: account?.name ?? '' })}</span>}
        </div>
        {error && <Warn>{error}</Warn>}
        {runs.map((r) => (
          <RunCard key={r.id} run={r} madeLabel={t('test.made')} />
        ))}
      </Stack>
    </section>
  )
}

const fieldCol: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0, flex: 1 }
